import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useDocuments,
  useUploadDocument,
  useArchiveDocument,
  useDownloadDocument,
} from '../../../platform/api/hooks';
import { asList, asRecord, str, getMeta } from '../../../platform/api/utils';
import { downloadBlob } from '../../../shared/download';
import {
  DOCUMENT_STATUS_LABELS,
  DOCUMENT_VISIBILITY_LABELS,
  labelOf,
} from '../../../shared/labels';
import { PageHeader } from '../../../components/ui/PageHeader';
import { Badge } from '../../../components/ui/Badge';
import { Section } from '../../../components/ui/Section';
import { EmptyState } from '../../../components/ui/EmptyState';
import { ApiError } from '../../../platform/api/client';

import {
  PagBtn,
  Skeleton,
  Td,
  Th,
  fieldLabel,
  fmtDate,
  formatBytes,
  friendlyMime,
  inputCss,
  modalOverlay,
  modalStyle,
  primaryBtn,
  secondaryBtn,
  smallBtn,
} from './document-list.support';

export function DocumentList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showUpload, setShowUpload] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState('INTERNAL');
  const [file, setFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState('');
  const [downloadError, setDownloadError] = useState('');

  const params: Record<string, unknown> = { page, limit: 20 };
  if (search) params.search = search;

  const { data, isLoading, isError } = useDocuments(params);
  const uploadDocument = useUploadDocument();
  const archiveDocument = useArchiveDocument();
  const downloadDocument = useDownloadDocument();

  const docs = asList(data);
  const meta = getMeta(data);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(1);
  }, []);

  async function handleUpload() {
    setUploadError('');
    if (!file) {
      setUploadError('Selecione um arquivo.');
      return;
    }
    // Fields before file so the multipart parser can finish without hanging
    // on an unconsumed file stream (common with busboy / @fastify/multipart).
    const form = new FormData();
    form.append('title', title.trim() || file.name);
    if (description.trim()) form.append('description', description.trim());
    form.append('visibility', visibility);
    form.append('file', file);

    try {
      await uploadDocument.mutateAsync(form);
      setShowUpload(false);
      setTitle('');
      setDescription('');
      setVisibility('INTERNAL');
      setFile(null);
      setUploadError('');
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Não foi possível enviar o documento.';
      setUploadError(msg);
    }
  }

  async function handleDownload(id: string, fileName: string) {
    setDownloadError('');
    try {
      const blob = await downloadDocument.mutateAsync(id);
      downloadBlob(blob, fileName);
    } catch (err) {
      setDownloadError(
        err instanceof ApiError ? err.message : 'Não foi possível baixar o documento.',
      );
    }
  }

  if (isError) {
    return (
      <div style={{ padding: '2.25rem 2.5rem', maxWidth: 1320 }}>
        <PageHeader title="Documentos" subtitle="Arquivos e documentos da casa" />
        <Section title="Erro">
          <p style={{ color: 'var(--danger)', fontSize: '0.875rem', margin: 0 }}>
            Não foi possível carregar a lista de documentos.
          </p>
        </Section>
      </div>
    );
  }

  const totalPages = Number(meta.totalPages) || 1;

  return (
    <div style={{ padding: '2.25rem 2.5rem', maxWidth: 1320 }}>
      <PageHeader
        title="Documentos"
        subtitle={`${str(meta.total, '0')} documentos armazenados`}
        action={{
          label: '+ Enviar arquivo',
          onClick: () => {
            setShowUpload(true);
            setUploadError('');
          },
        }}
      />

      <p style={{ fontSize: '0.8rem', color: 'var(--muted)', margin: '0 0 1rem' }}>
        Antes de anexar, confirme se este documento deve ser digitalizado e quem pode acessá-lo. Não
        envie fundamento ou conteúdo ritualístico sensível.
      </p>

      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
        <input
          type="text"
          placeholder="Buscar por título..."
          value={search}
          onChange={(e) => {
            handleSearch(e.target.value);
          }}
          className="filter-input"
          style={{ flex: 1, maxWidth: 340 }}
        />
      </div>

      {downloadError && (
        <p style={{ color: 'var(--danger)', fontSize: '0.8rem', margin: '0 0 1rem' }}>
          {downloadError}
        </p>
      )}

      {isLoading && <Skeleton />}

      {!isLoading && (
        <Section title="Documentos">
          {docs.length === 0 ? (
            <EmptyState
              title="Nenhum documento armazenado ainda."
              copy="Use “Enviar arquivo” para adicionar o primeiro documento administrativo da casa."
            />
          ) : (
            <>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid var(--line-soft)' }}>
                    <Th>Documento</Th>
                    <Th>Tipo</Th>
                    <Th>Visibilidade</Th>
                    <Th>Situação</Th>
                    <Th>Tamanho</Th>
                    <Th>Atualizado em</Th>
                    <Th>Ações</Th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((raw, i) => {
                    const d = asRecord(raw);
                    const id = str(d.id);
                    return (
                      <tr
                        key={id || String(i)}
                        style={{ borderBottom: '1px solid var(--soft)', cursor: 'pointer' }}
                        onClick={() => {
                          void navigate(`/documentos/${id}`);
                        }}
                      >
                        <Td>
                          <span style={{ fontWeight: 500, color: 'var(--green)' }}>
                            {str(d.title)}
                          </span>
                          {str(d.originalFileName) && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--faint)' }}>
                              {str(d.originalFileName)}
                            </div>
                          )}
                        </Td>
                        <Td style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                          {friendlyMime(str(d.mimeType))}
                        </Td>
                        <Td>
                          <Badge
                            label={labelOf(DOCUMENT_VISIBILITY_LABELS, d.visibility)}
                            variant="info"
                          />
                        </Td>
                        <Td>
                          <Badge
                            label={labelOf(DOCUMENT_STATUS_LABELS, d.status)}
                            variant={str(d.status) === 'ACTIVE' ? 'success' : 'default'}
                          />
                        </Td>
                        <Td style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                          {formatBytes(d.sizeBytes)}
                        </Td>
                        <Td style={{ fontSize: '0.85rem', color: 'var(--muted)' }}>
                          {fmtDate(d.updatedAt ?? d.createdAt)}
                        </Td>
                        <Td>
                          <div style={{ display: 'flex', gap: '0.35rem' }}>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                void handleDownload(id, str(d.originalFileName, str(d.title)));
                              }}
                              style={smallBtn}
                              disabled={downloadDocument.isPending}
                            >
                              Baixar
                            </button>
                            {str(d.status) === 'ACTIVE' && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm('Arquivar este documento?')) {
                                    archiveDocument.mutate(id);
                                  }
                                }}
                                style={{
                                  ...smallBtn,
                                  color: 'var(--danger)',
                                  borderColor: 'var(--danger-soft)',
                                }}
                                disabled={archiveDocument.isPending}
                              >
                                Arquivar
                              </button>
                            )}
                          </div>
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {totalPages > 1 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '0.25rem',
                    marginTop: '1rem',
                  }}
                >
                  <PagBtn
                    disabled={page <= 1}
                    onClick={() => {
                      setPage(page - 1);
                    }}
                    label="<"
                  />
                  {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((p) => (
                    <PagBtn
                      key={p}
                      active={p === page}
                      onClick={() => {
                        setPage(p);
                      }}
                      label={String(p)}
                    />
                  ))}
                  <PagBtn
                    disabled={page >= totalPages}
                    onClick={() => {
                      setPage(page + 1);
                    }}
                    label=">"
                  />
                </div>
              )}
            </>
          )}
        </Section>
      )}

      {showUpload && (
        <div
          style={modalOverlay}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowUpload(false);
          }}
        >
          <div style={modalStyle}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.05rem' }}>Enviar documento</h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.8rem', color: 'var(--muted)' }}>
              PDF, imagens, texto ou Office até 10 MB.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <label style={fieldLabel}>
                Arquivo *
                <input
                  type="file"
                  onChange={(e) => {
                    setFile(e.target.files?.[0] ?? null);
                  }}
                  style={{ ...inputCss, padding: '0.4rem' }}
                />
              </label>
              <label style={fieldLabel}>
                Título
                <input
                  value={title}
                  onChange={(e) => {
                    setTitle(e.target.value);
                  }}
                  placeholder={file?.name ?? 'Título do documento'}
                  style={inputCss}
                />
              </label>
              <label style={fieldLabel}>
                Descrição (opcional)
                <textarea
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                  }}
                  style={{ ...inputCss, minHeight: 64, resize: 'vertical' }}
                />
              </label>
              <label style={fieldLabel}>
                Visibilidade
                <select
                  value={visibility}
                  onChange={(e) => {
                    setVisibility(e.target.value);
                  }}
                  style={inputCss}
                >
                  {Object.entries(DOCUMENT_VISIBILITY_LABELS).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </select>
              </label>
              {uploadError && (
                <p style={{ color: 'var(--danger)', fontSize: '0.8rem', margin: 0 }}>
                  {uploadError}
                </p>
              )}
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowUpload(false);
                  }}
                  style={secondaryBtn}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => {
                    void handleUpload();
                  }}
                  disabled={uploadDocument.isPending}
                  style={primaryBtn}
                >
                  {uploadDocument.isPending ? 'Enviando...' : 'Enviar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
